import { Box, HStack } from '@chakra-ui/react'
import { BaseEdge, EdgeLabelRenderer, getBezierPath, MarkerType, useReactFlow } from '@xyflow/react'
import { Bold, MoreHorizontal, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useColorModeValue } from '~/color-mode'

// Predefined color options for edges
const EDGE_COLORS = [
  { name: 'Dark Gray', value: '#383838' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Purple', value: '#A855F7' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Yellow', value: '#FFC000' },
  { name: 'Teal', value: '#14B8A6' },
]

export default function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}) {
  const { setEdges } = useReactFlow()
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Chrome colors (menu chip UI, not diagram data) - theme-aware for dark mode
  const menuBorderColor = useColorModeValue('#E0DFDF', '#5F5D5A')
  const menuIconColor = useColorModeValue('#777672', '#BBB9B5')
  const menuActiveBg = useColorModeValue('#E0DFDF', '#5F5D5A')
  const menuHoverBg = useColorModeValue('#F0F0EF', '#4A4743')
  const menuTextColor = useColorModeValue('#5F5D5A', '#CDCCC9')
  const dangerTextColor = useColorModeValue('#DE2C32', '#FD948B')
  const dangerHoverBg = useColorModeValue('#FDECE8', '#601F1D')

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  // Get label, color, and bold from edge data (persisted)
  const label = data?.label || ''
  const edgeColor = data?.color || (style as { stroke?: string })?.stroke || '#383838'
  const isBold = data?.bold || false

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [label])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [showMenu])

  const onLabelChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newLabel = e.target.value
    // Update the edge's data to persist the label
    setEdges((edges) =>
      edges.map((edge) =>
        edge.id === id ? { ...edge, data: { ...edge.data, label: newLabel } } : edge,
      ),
    )
  }

  const onColorChange = (color: string) => {
    // Update the edge's color in both style and data
    setEdges((edges) =>
      edges.map((edge) =>
        edge.id === id
          ? {
              ...edge,
              style: { ...edge.style, stroke: color },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: color,
              },
              data: { ...edge.data, color: color },
            }
          : edge,
      ),
    )
    setShowMenu(false)
  }

  const onToggleBold = () => {
    const newBold = !isBold
    setEdges((edges) =>
      edges.map((edge) =>
        edge.id === id
          ? {
              ...edge,
              style: { ...edge.style, strokeWidth: newBold ? 3 : 1 },
              data: { ...edge.data, bold: newBold },
            }
          : edge,
      ),
    )
  }

  const onDeleteEdge = () => {
    setEdges((edges) => edges.filter((edge) => edge.id !== id))
  }

  const onMenuButtonClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    setShowMenu(!showMenu)
  }

  // Apply the stored color and strokeWidth to the edge style
  const edgeStyle = {
    ...style,
    stroke: edgeColor,
    strokeWidth: isBold ? 3 : 1,
  }

  // Create markerEnd with the correct color
  const coloredMarkerEnd =
    typeof markerEnd === 'object' ? { ...markerEnd, color: edgeColor } : markerEnd

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={coloredMarkerEnd} style={edgeStyle} />
      <EdgeLabelRenderer>
        <div
          className="button-edge__label nodrag nopan"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            zIndex: showMenu ? 9999 : 1,
          }}
        >
          <HStack gap={1}>
            <textarea
              ref={textareaRef}
              id="text"
              name="componentName"
              onChange={onLabelChange}
              value={label}
              className="nodrag pg-transparent-edge-input pg-edge-textarea"
              rows={1}
            />
            {/* Menu button */}
            <Box position="relative" ref={menuRef}>
              <button
                type="button"
                className="button-edge__menu-button"
                onClick={onMenuButtonClick}
                style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  border: `1px solid ${menuBorderColor}`,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Edge options"
              >
                <MoreHorizontal size={12} color={menuIconColor} />
              </button>
              {showMenu && (
                <Box
                  position="absolute"
                  top="24px"
                  right="0"
                  bg="bg.panel"
                  borderRadius="8px"
                  boxShadow="0 4px 12px rgba(0,0,0,0.15)"
                  p={2}
                  zIndex={10000}
                  minW="150px"
                >
                  {/* Color section */}
                  <Box fontSize="xs" color="content.secondary" mb={1.5} fontWeight="medium">
                    Color
                  </Box>
                  <Box display="grid" gridTemplateColumns="repeat(5, 1fr)" gap={1} mb={2}>
                    {EDGE_COLORS.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        onClick={() => onColorChange(color.value)}
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          backgroundColor: color.value,
                          border:
                            edgeColor === color.value
                              ? '2px solid var(--chakra-colors-gray-900)'
                              : '1px solid var(--chakra-colors-gray-200)',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                        title={color.name}
                      />
                    ))}
                  </Box>
                  {/* Divider */}
                  <Box borderTopWidth="1px" borderColor="gray.200" my={1.5} />
                  {/* Bold toggle button */}
                  <button
                    type="button"
                    onClick={onToggleBold}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 8px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: isBold ? menuActiveBg : 'transparent',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: menuTextColor,
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isBold) e.currentTarget.style.backgroundColor = menuHoverBg
                    }}
                    onMouseLeave={(e) => {
                      if (!isBold) e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <Bold size={14} />
                    Bold
                    {isBold && <span style={{ marginLeft: 'auto', fontSize: '11px' }}>✓</span>}
                  </button>
                  {/* Divider */}
                  <Box borderTopWidth="1px" borderColor="gray.200" my={1.5} />
                  {/* Delete button */}
                  <button
                    type="button"
                    onClick={onDeleteEdge}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 8px',
                      borderRadius: '4px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: dangerTextColor,
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = dangerHoverBg)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </Box>
              )}
            </Box>
          </HStack>
        </div>
      </EdgeLabelRenderer>
    </>
  )
}
